import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { useConfig } from "../config.tsx";

/** False until the first page has rendered: the initial load keeps the browser's default focus. */
let hasRenderedFirstPage = false;

export function useDocumentTitle(title: string) {
  const { displayName } = useConfig();
  useEffect(() => {
    document.title = title === displayName ? displayName : `${title} · ${displayName}`;
  }, [title, displayName]);
}

/** Moves focus to `ref` when it mounts after a client-side navigation (not on the first load). */
export function useFocusOnNavigation(ref: RefObject<HTMLElement | null>, enabled = true) {
  useLayoutEffect(() => {
    if (enabled && hasRenderedFirstPage) ref.current?.focus();
    hasRenderedFirstPage = true;
  }, [ref, enabled]);
}

/**
 * Renders the page's single <h1> and keeps document.title in sync (WCAG 2.4.2). After client-side
 * navigation, focus moves to the new heading so keyboard and screen-reader users start at the new
 * content (WCAG 2.4.3). Doing this when the heading mounts avoids racing the router's transition.
 */
export function PageTitle({ children }: { children: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(children);
  useFocusOnNavigation(ref);

  return (
    <h1 ref={ref} tabIndex={-1} className="page-title">
      {children}
    </h1>
  );
}
